import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowRight, Trophy, Users, Clock, Check, Flame, Sparkles,
  Award, Zap, ChevronRight, Timer, Snowflake,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Small internal helpers
// ---------------------------------------------------------------------------
/** Animated integer that eases from prev → next whenever the target changes. */
function AnimatedCount({ value, className, testId }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);
  useTransform(mv, (v) => Math.round(v));
  useEffect(() => {
    const controls = animate(mv, value, {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [value, mv]);
  return <span data-testid={testId} className={className}>{display.toLocaleString()}</span>;
}

/** Circular progress ring for the streak → next-milestone visualisation. */
function ProgressRing({ pct, accent, size = 72, stroke = 6, children }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * circumference;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          stroke="rgba(255,255,255,0.10)" strokeWidth={stroke} fill="none"
        />
        <motion.circle
          cx={size / 2} cy={size / 2} r={radius}
          stroke={accent} strokeWidth={stroke} fill="none"
          strokeLinecap="round"
          initial={{ strokeDasharray: `0 ${circumference}` }}
          animate={{ strokeDasharray: `${dash} ${circumference}` }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}

const DIFFICULTY_STYLE = {
  Easy:     { bg: 'rgba(94,234,212,0.12)', fg: '#5EEAD4', label: 'Easy' },
  Medium:   { bg: 'rgba(253,186,116,0.12)', fg: '#FDBA74', label: 'Medium' },
  Creative: { bg: 'rgba(167,139,250,0.14)', fg: '#C4B5FD', label: 'Creative' },
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function DailyChallenge({ navigate, token }) {
  const [ch, setCh] = useState(null);
  const [joining, setJoining] = useState(false);
  const [confetti, setConfetti] = useState(false);
  // Admin-editable off-theme photo IDs. Falls back to a small hard-coded
  // list if the endpoint is unreachable so the guard still fires.
  const [blocklist, setBlocklist] = useState(['photo-1519638831568-d9897f54ed69']);
  const containerRef = useRef(null);
  // countdown removed — mindful design: no ticking clocks on challenge cards

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/challenges/today`, { headers });
        if (!cancelled) setCh(res.data);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          // soft-fail — the skeleton renders while we retry
          console.warn('[daily-challenge] fetch failed', err?.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Fetch the admin-editable off-theme photo blocklist. Public endpoint,
  // cached 120s server-side, so this is a cheap read that stays in sync
  // whenever an admin adds/removes an ID from /admin/challenges/blocklist.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/challenge-artwork-blocklist`)
      .then((r) => { if (!cancelled) setBlocklist(r.data?.photo_ids || []); })
      .catch(() => { /* keep hard-coded fallback */ });
    return () => { cancelled = true; };
  }, []);

  const handleJoin = async () => {
    if (!token) { navigate('/auth?mode=signup&intent=challenge'); return; }
    if (ch?.joined) {
      navigate(`/create?type=${ch.media === 'video' ? 'video' : 'image'}&prompt=${encodeURIComponent(ch.prompt)}`);
      return;
    }
    setJoining(true);
    try {
      const res = await axios.post(`${API}/challenges/join`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCh(res.data);
      const badgeText = res.data.badge_awarded
        ? ` · Badge unlocked: ${res.data.badge_label}`
        : '';
      toast.success(`+${res.data.reward_xp} ${res.data.reward_type}${badgeText}`, {
        duration: 3200,
      });
      // Fire the confetti overlay for milestone streaks and first-time badges.
      const hitMilestone = [3, 7, 14, 30, 60, 100, 200, 365].includes(res.data.streak?.current);
      if (res.data.badge_awarded || hitMilestone) {
        setConfetti(true);
        setTimeout(() => setConfetti(false), 2400);
      }
    } catch (err) {
      toast.error('Could not join the challenge. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  if (!ch) {
    return (
      <section data-testid="daily-challenge-section" className="relative py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
          <div className="h-72 bg-[#101F1D]/60 border border-white/10 animate-pulse" />
        </div>
      </section>
    );
  }

  const streak = ch.streak || { current: 0, longest: 0, next_milestone: 3, progress_pct: 0 };
  const diffStyle = DIFFICULTY_STYLE[ch.difficulty] || DIFFICULTY_STYLE.Medium;
  const joinLabel = !token
    ? 'Sign up to join'
    : ch.joined
      ? 'Create your entry'
      : joining
        ? 'Joining…'
        : 'Join today\u2019s challenge';

  return (
    <section
      ref={containerRef}
      data-testid="daily-challenge-section"
      className="relative py-6 md:py-8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '0px 0px -80px 0px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden border border-white/10"
          style={{
            background: `linear-gradient(135deg, ${ch.background} 0%, #0A1514 55%, #0A1514 100%)`,
          }}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -top-28 -right-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-25"
            style={{ backgroundColor: ch.accent }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -left-24 w-[360px] h-[360px] rounded-full blur-3xl opacity-15"
            style={{ backgroundColor: ch.accent }}
          />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-0">
            {/* Featured artwork panel. Two ways an off-theme image gets
                swapped for an atmospheric fallback:
                  1. URL slug matches a gadget keyword (camera / laptop /
                     smartphone / …) — catches product-photo naming.
                  2. Exact photo-id is on the KNOWN_OFF_THEME set — catches
                     Unsplash images whose slug is all-numeric so the regex
                     alone can't match them (e.g. the Canon-DSLR photo
                     surfaced by testing_agent iter_49).
                Admin can pick a better artwork in /admin/challenges. */}
            {(() => {
              const raw = ch.featured_artwork_url || '';
              const gadgetPattern = /(camera|laptop|smartphone|iphone|gadget|device|keyboard|monitor|tablet|earbuds|headphone|product-photo)/i;
              const KNOWN_OFF_THEME = blocklist;
              const isOffTheme = gadgetPattern.test(raw)
                || KNOWN_OFF_THEME.some((id) => raw.includes(id));
              const fallback = 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=1400&q=80';
              const artworkUrl = isOffTheme ? fallback : raw;
              return (
                <div className="relative lg:col-span-5 min-h-[260px] lg:min-h-[520px]">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={ch.id + (artworkUrl === fallback ? ':fallback' : '')}
                      data-testid="challenge-artwork"
                      src={artworkUrl}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="eager"
                      initial={{ opacity: 0, scale: 1.06 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </AnimatePresence>
                  {/* readability gradients */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1514] via-transparent to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0A1514]/60 lg:to-[#0A1514]" />

              {/* Weekly theme only. The old right-side "type" pill has been
                  removed because it was surfacing urgency labels like
                  "Limited Time" that conflicted with the calm brand. */}
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between gap-2">
                <div
                  data-testid="challenge-weekly-theme"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/15 text-[10px] uppercase tracking-[0.22em] font-semibold text-white"
                >
                  <span>{ch.weekly_theme_emoji}</span>
                  {ch.weekly_theme}
                </div>
              </div>
                </div>
              );
            })()}

            {/* Right / content panel */}
            <div className="lg:col-span-7 p-6 md:p-10 lg:p-12 flex flex-col">
              <div className="flex items-center gap-2.5">
                <span
                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl"
                  style={{ backgroundColor: `${ch.accent}22`, color: ch.accent }}
                >
                  <Trophy className="w-4 h-4" />
                </span>
                <span
                  className="text-[10px] tracking-[0.22em] uppercase font-semibold"
                  style={{ color: ch.accent }}
                >
                  Daily Challenge · Day {ch.day}
                </span>
              </div>

              <h2
                data-testid="challenge-title"
                className="mt-4 text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-tight"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {ch.title}
              </h2>
              <p
                data-testid="challenge-prompt"
                className="mt-3 text-base md:text-lg text-white/85 leading-relaxed italic"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                &ldquo;{ch.prompt}&rdquo;
              </p>

              {/* Meta chips — reward + badge callouts removed per Aug-2026
                  homepage cleanup (softer, less gamified tone). Difficulty
                  + estimated time only. */}
              <div className="mt-5 flex flex-wrap items-center gap-2 text-[11px] font-semibold">
                <span
                  data-testid="challenge-difficulty"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border"
                  style={{
                    backgroundColor: diffStyle.bg,
                    color: diffStyle.fg,
                    borderColor: `${diffStyle.fg}44`,
                  }}
                >
                  <Zap className="w-3 h-3" />
                  {diffStyle.label}
                </span>
                <span
                  data-testid="challenge-time"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/80"
                >
                  <Timer className="w-3 h-3" />
                  {ch.estimated_minutes}–{ch.estimated_minutes + 3} min
                </span>
              </div>

              {/* Calm framing — no ticking clock (mindful-design audit) */}
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#94A3B8]">
                <span
                  data-testid="challenge-countdown"
                  className="inline-flex items-center gap-1.5"
                >
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-white/95 font-semibold">Today&apos;s challenge</span>
                  · new one tomorrow
                </span>
                <span
                  data-testid="challenge-participants"
                  className="inline-flex items-center gap-1.5"
                >
                  <Users className="w-3.5 h-3.5" />
                  <AnimatedCount value={ch.participants_count} className="text-white/95 font-semibold" testId="challenge-participants-count" />
                  joined today
                </span>
                <span
                  data-testid="challenge-creations"
                  className="inline-flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <AnimatedCount value={ch.creations_count} className="text-white/95 font-semibold" testId="challenge-creations-count" />
                  creations
                </span>
              </div>

              {/* Motivation line */}
              <p
                data-testid="challenge-motivation"
                className="mt-6 text-sm md:text-base text-white/85 leading-relaxed"
              >
                {ch.motivation}
              </p>

              {/* Streak + CTA row — streak stays as a quiet acknowledgement
                  of consistency (no flame icon, no "to next milestone"
                  countdown per Aug-2026 homepage cleanup). Deeper stats
                  live on the logged-in profile page. */}
              <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6">
                {streak.current > 0 && (
                  <div className="flex items-center gap-4">
                    <ProgressRing pct={streak.progress_pct} accent={ch.accent} size={60} stroke={4}>
                      <span data-testid="challenge-streak" className="text-sm font-semibold text-white tabular-nums">
                        {streak.current}
                      </span>
                    </ProgressRing>
                    <div className="text-xs text-[#94A3B8] leading-snug">
                      <p className="text-white/90 font-semibold text-sm">
                        You&rsquo;ve shown up {streak.current} {streak.current === 1 ? 'day' : 'days'} running
                      </p>
                      <p className="text-white/50">Nice rhythm.</p>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2 sm:ml-auto sm:items-end">
                  <motion.button
                    data-testid="challenge-join-btn"
                    onClick={handleJoin}
                    disabled={joining}
                    whileHover={{ scale: joining ? 1 : 1.03 }}
                    whileTap={{ scale: joining ? 1 : 0.97 }}
                    className={`inline-flex items-center justify-center gap-2 font-semibold rounded-full px-7 py-3.5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                      ch.joined
                        ? 'bg-white/10 text-white border border-white/20 hover:bg-white/15'
                        : 'text-[#0A1514] hover:opacity-95'
                    }`}
                    style={ch.joined ? undefined : {
                      backgroundColor: ch.accent,
                      boxShadow: `0 0 24px ${ch.accent}55`,
                    }}
                  >
                    {ch.joined && <Check className="w-4 h-4" />}
                    {joinLabel}
                    {!ch.joined && <ArrowRight className="w-4 h-4" />}
                  </motion.button>
                  <button
                    data-testid="challenge-view-entries-btn"
                    onClick={() => navigate('/trending')}
                    className="inline-flex items-center justify-center gap-1 text-xs text-white/70 hover:text-white transition-colors"
                  >
                    See entries <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Confetti flourish on milestone / first badge */}
          <AnimatePresence>
            {confetti && (
              <motion.div
                data-testid="challenge-confetti"
                className="pointer-events-none absolute inset-0 z-30 overflow-hidden"
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {Array.from({ length: 40 }).map((_, i) => (
                  <ConfettiDot key={`confetti-dot-${i}`} accent={ch.accent} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}

function ConfettiDot({ accent }) {
  const { x0, x1, y1, delay, size, hue } = useMemo(() => ({
    x0: 50 + (Math.random() - 0.5) * 40,
    x1: Math.random() * 100,
    y1: 100 + Math.random() * 20,
    delay: Math.random() * 0.4,
    size: 4 + Math.random() * 6,
    hue: Math.random() > 0.5 ? accent : '#FFD166',
  }), [accent]);
  return (
    <motion.span
      className="absolute rounded-full"
      style={{
        left: `${x0}%`,
        top: '15%',
        width: size,
        height: size,
        backgroundColor: hue,
      }}
      initial={{ opacity: 0, y: 0, x: 0, scale: 0.5 }}
      animate={{
        opacity: [0, 1, 1, 0],
        y: `${y1}vh`,
        x: `${(x1 - x0) * 4}px`,
        rotate: Math.random() * 720,
        scale: 1,
      }}
      transition={{ duration: 1.8, delay, ease: 'easeOut' }}
    />
  );
}
